import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {StyleClassModule} from 'primeng/styleclass';
import {LayoutService} from '../../shared/services/layout.service';
import {DropdownModule} from 'primeng/dropdown';
import {FormsModule} from '@angular/forms';
import {TooltipModule} from 'primeng/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {LanguageSwitcherComponent} from '../../shared/components/language-switcher/language-switcher.component';
import { Router } from '@angular/router';
import {SharedDataService} from '../../services/shared-data.service';
import {Agent, User} from '../../models/users';
import {AgentStateManagerService} from '../../services/agent-state-manager.service';
import {AgentState, STATE_COLOR} from '../../models/chat';
import {interval, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, map, shareReplay, takeUntil, tap} from 'rxjs/operators';
import { AgentStateService } from '../../services/agent-state.service';
import { WorkspaceService } from '../../services/workspace.service';

const LS_ALL_STATES = 'all_status_agent';
const LS_STATUS_AGENT = 'status_agent';
const LS_LAST_UPDATE = 'last_update';

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    DropdownModule,
    FormsModule,
    TooltipModule,
    TranslateModule,
    LanguageSwitcherComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss']
})
export class TopBarComponent implements OnInit, AfterViewInit, OnDestroy {
  states$: Observable<AgentState[]> | undefined;
  selectedState!: AgentState;
  items!: MenuItem[];
  user: User | null = null;
  isStatusChanging = false;

  // Timer display
  elapsedTime = '00:00:00';
  private timerSub?: Subscription;
  private lastUpdate?: Date;

  private destroy$ = new Subject<void>();

  @ViewChild('userInfoContainer', { static: false }) userInfoContainer?: ElementRef<HTMLDivElement>;
  private userInfoWidth = 0;

  // Store the bound function to ensure the same reference is used for add/remove
  private boundUpdateUserInfoWidth = this.updateUserInfoWidth.bind(this);



  get getUsername(): string {
    const username = this.user?.username || '';
    if (!username) return '';

    // If username is short, return as is
    if (username.length <= 14) return username;

    // If enough horizontal space, show in single line
    if (this.shouldShowSingleLine(username)) return username;

    // If username contains spaces, split by words for better line breaks
    if (username.includes(' ')) {
      const words = username.split(' ');
      let firstLine = '';
      let secondLine = '';
      let totalLen = 0;
      let halfLen = Math.ceil(username.length / 2);

      for (let i = 0; i < words.length; i++) {
        // +1 for space except first word
        let wordLen = words[i].length + (i === 0 ? 0 : 1);
        if (totalLen + wordLen <= halfLen || firstLine === '') {
          firstLine += (firstLine ? ' ' : '') + words[i];
          totalLen += wordLen;
        } else {
          secondLine = words.slice(i).join(' ');
          break;
        }
      }
      if (secondLine) {
        return `${firstLine}\n${secondLine}`;
      } else {
        return firstLine;
      }
    }

    // Try to split at '.' or '_'
    const splitChar = username.includes('.') ? '.' : (username.includes('_') ? '_' : '');
    if (splitChar) {
      const [first, ...rest] = username.split(splitChar);
      const second = rest.join(splitChar);
      if (first.length <= 14 && second.length <= 14) {
        return `${first}\n${second}`;
      }
    }

    // Otherwise, split in the middle for two lines
    const mid = Math.floor(username.length / 2);
    return `${username.slice(0, mid)}\n${username.slice(mid)}`;
  }

  private shouldShowSingleLine(username: string): boolean {
    // If container is wide enough for username, show single line
    // Assume average character width ~8px + padding, adjust as needed
    const minWidth = Math.max(140, username.length * 9);
    return this.userInfoWidth > minWidth;
  }

  constructor(
    public layoutService: LayoutService,
    private sharedDataService: SharedDataService,
    private agentStateManagerService: AgentStateManagerService,
    private agentStateService: AgentStateService,
    private workspaceService: WorkspaceService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeMenu();

    this.user = this.sharedDataService.getAgent();
    if (!this.user) {
      console.error('User data is not available in top-bar component');
      return;
    }

    // Prepare states$ stream, caching in localStorage if needed
    const savedStates = JSON.parse(localStorage.getItem(LS_ALL_STATES) || '[]') as AgentState[];
    if (savedStates.length) {
      this.states$ = of(savedStates);
    } else {
      this.states$ = this.agentStateManagerService.getAgentState().pipe(
        map(labels =>
          labels.map((label, i) => ({
            label,
            value: label.toUpperCase(),
            description: `Description for ${label}`,
            categoryId: `category-${i}`,
            color: STATE_COLOR[i % STATE_COLOR.length].color,
            backgroundColor: STATE_COLOR[i % STATE_COLOR.length].backgroundColor
          }))
        ),
        tap(states => localStorage.setItem(LS_ALL_STATES, JSON.stringify(states))),
        shareReplay(1)
      );
    }

    // Initialize selected state once we have the states list
    this.states$
      .pipe(takeUntil(this.destroy$))
      .subscribe(states => this.initializeSelectedState(states));

    // Restore and start timer if we have a saved timestamp
    const savedTs = localStorage.getItem(LS_LAST_UPDATE);
    if (savedTs) {
      const iso = savedTs.endsWith('Z') ? savedTs : savedTs + 'Z';
      this.lastUpdate = new Date(iso);
      this.startTimer();
    }

  }

  ngAfterViewInit(): void {
    this.ensureDropdownTheme();
    this.adjustDropdownWidth();
    this.updateUserInfoWidth();
    window.addEventListener('resize', this.boundUpdateUserInfoWidth);
  }

  ngOnDestroy(): void {
    this.timerSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.boundUpdateUserInfoWidth);
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    // Reserved for future use
  }

  private initializeMenu(): void {
    this.items = [
      { label: 'Profile', icon: 'pi pi-user', command: () => this.navigateToProfile() },
      { label: 'Settings', icon: 'pi pi-cog', command: () => this.navigateToSettings() },
      { label: 'Logout', icon: 'pi pi-sign-out', command: () => this.logout() }
    ];
  }

  private initializeSelectedState(states: AgentState[]): void {
    const savedLabel = localStorage.getItem(LS_STATUS_AGENT);
    const found = states.find(s => s.label === savedLabel);
    this.selectedState = found ?? states[0];
    localStorage.setItem(LS_STATUS_AGENT, this.selectedState.label);
    this.cdr.markForCheck();
  }

  onStateChange(event: any): void {
    const newState = event.value as AgentState;
    if (!newState || !this.user) {
      return;
    }

    // Optimistic UI update
    this.selectedState = newState;
    this.isStatusChanging = true;

    this.agentStateManagerService
      .setAgentState(this.user.id, newState.label)
      .pipe(
        takeUntil(this.destroy$),
        tap((response: User) => {

          this.sharedDataService.updateAgentState(response);
          this.agentStateService.setAgentState(newState.label);

          const lastUpdate = new Date(response.last_update); // 2025-07-16T16:49:54


          this.lastUpdate = lastUpdate;

          // Save timestamp to localStorage for persistence
          localStorage.setItem(LS_LAST_UPDATE, lastUpdate.toISOString());

          this.startTimer();

          // Persist current state label
          localStorage.setItem(LS_STATUS_AGENT, newState.label);
        }),
        catchError(err => {
          console.error('Error updating agent state:', err);
          return of(null);
        }),
        tap(() => (this.isStatusChanging = false))
      )
      .subscribe();
  }

  /** Timer helpers **/
  private startTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateElapsed());
  }

  private updateElapsed(): void {
    if (!this.lastUpdate) {
      this.elapsedTime = '00:00:00';
      return;
    }

    // Convert current time to UTC-5 (COT)
    const nowUTC = new Date();

    // console.log('Current time (UTC):', nowUTC);
    // console.log('Last Update:', this.lastUpdate);

    const totalSeconds = Math.floor((nowUTC.getTime() - this.lastUpdate.getTime()) / 1000);
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');

    this.elapsedTime = `${hours}:${minutes}:${seconds}`;
    this.cdr.markForCheck();
  }

  /** Dark theme & width adjustments (unchanged) **/
  private ensureDropdownTheme(): void {
    // ... existing implementation ...
  }

  public adjustDropdownWidth(): void {
    // ... existing implementation ...
  }

  /** Navigation placeholders **/
  private navigateToProfile(): void {
    // ...
  }

  private navigateToSettings(): void {
    // ...
  }

  private logout(): void {
    // ...
  }

  changeWorkspace(): void {
    this.workspaceService.clearWorkspaceId();
    localStorage.setItem('worksel', 'pending');
    this.router.navigate(['/workspace-selection'], {
      queryParams: {
        changeWorkspace: 'true'
      }
    });
  }

  private updateUserInfoWidth() {
    if (this.userInfoContainer && this.userInfoContainer.nativeElement) {
      this.userInfoWidth = this.userInfoContainer.nativeElement.offsetWidth;
      this.cdr.markForCheck();
    }
  }
}
